import { CLASS_NAMES } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Card module
 */
export default class StatisticsAnimation extends MessageBus {
  /**
   * Constructor function of the CardFunc class. Must receive one arguments, a DOM
   * node representing a delivery ticket.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {object} element DOM node that is passed into the constructor
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} CardFunc A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} CardFunc A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    const statisticsWrapper = this.element.querySelector(
      `.${CLASS_NAMES.HOME_STATISTICS_WRAPPER}`
    );
    this.cards = statisticsWrapper.querySelectorAll(
      `.${CLASS_NAMES.HOME_STATISTICS_CARD}`
    );
    this.rows = statisticsWrapper.querySelectorAll(
      `.${CLASS_NAMES.HOME_STATISTICS_CARD}:nth-child(2n+1), .${CLASS_NAMES.HOME_STATISTICS_CARD}:nth-child(2n+2)`
    );
    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} Search A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.createIntersectionObserver();
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleIntersection = this.handleIntersection.bind(this);
    return this;
  }

  /**
   * Creates and initializes the Intersection Observer
   * @private
   */
  createIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };

    const observer = new IntersectionObserver(this.handleIntersection, options);
    if (this.cards) {
      this.cards.forEach((card) => {
        observer.observe(card);
      });
    }
    if (this.rows) {
      this.rows.forEach((row) => {
        observer.observe(row);
      });
    }
  }

  /**
   * Handles the intersection observer callback
   * @returns {object} A reference to the current instance of the class
   * @param {IntersectionObserverEntry[]} entries - The list of observed DOM elements
   * @private
   */
  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add(CLASS_NAMES.IS_VISIBLE);
      }
    });

    return this;
  }
}
